import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Crosshair, X, Sparkles } from "lucide-react";

export interface TargetNotification {
  title: string;
  message: string;
  projectName: string;
  categoryName: string;
  subcategoryName: string;
  targetType: string;
  targetValue: number;
  currentPercentage: number;
  currentAmount: number;
  budget: number;
  customMessage: string;
  triggeredBy: string;
  dateTime: string;
  deepLink: string;
}

interface Props {
  notification: TargetNotification;
  onClose: () => void;
  autoCloseMs?: number;
}

export function TargetNotificationPopup({
  notification,
  onClose,
  autoCloseMs = 20000,
}: Props) {
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);

  // Auto-dismiss countdown
  useEffect(() => {
    if (autoCloseMs <= 0) return;
    const interval = 50;
    const decrement = (interval / autoCloseMs) * 100;
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          handleClose();
          return 0;
        }
        return prev - decrement;
      });
    }, interval);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCloseMs]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  };



  const n = notification;
  const thresholdLabel =
    n.targetType === "percentage"
      ? `${n.targetValue}%`
      : `$${Number(n.targetValue).toLocaleString()}`;
  const currentLabel =
    n.targetType === "percentage"
      ? `${n.currentPercentage.toFixed(1)}%`
      : `$${n.currentAmount.toLocaleString()}`;

  // Progress ratio for visual bar
  const ratio = n.targetType === "percentage"
    ? Math.min(n.currentPercentage / 100, 1)
    : n.budget > 0 ? Math.min(n.currentAmount / n.budget, 1) : 0;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 99997,
          background: "rgba(15, 23, 42, 0.12)",
          backdropFilter: "blur(2px)",
          animation: isExiting
            ? "notifBackdropExit 0.3s ease forwards"
            : "notifBackdropEnter 0.3s ease forwards",
        }}
        onClick={handleClose}
      />
      {/* Popup */}
      <div
        style={{
          position: "fixed",
          top: 32,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 99998,
          width: 420,
          maxWidth: "92vw",
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(30,115,190,0.08), 0 0 40px rgba(245,158,11,0.06)",
          overflow: "hidden",
          animation: isExiting
            ? "notifPopupExit 0.3s ease-in forwards"
            : "notifPopupEnter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
        }}
      >
        {/* Auto-dismiss progress bar */}
        <div
          style={{
            height: 3,
            background: "#f1f5f9",
            position: "relative",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.max(progress, 0)}%`,
              background: "linear-gradient(90deg, #1e73be, #3b8fd4)",
              borderRadius: 2,
              transition: "width 50ms linear",
            }}
          />
        </div>

        {/* Header */}
        <div
          style={{
            padding: "16px 18px 12px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.04))",
              border: "1px solid rgba(245,158,11,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Crosshair size={18} style={{ color: "#d97706" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#1e293b",
                lineHeight: 1.3,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Target Threshold Reached
              <Sparkles size={14} style={{ color: "#f59e0b" }} />
            </div>
            <div
              style={{
                fontSize: 11,
                color: "#94a3b8",
                marginTop: 2,
              }}
            >
              {n.dateTime}
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: 6,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#f1f5f9";
              (e.currentTarget as HTMLElement).style.color = "#64748b";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#f8fafc";
              (e.currentTarget as HTMLElement).style.color = "#94a3b8";
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "14px 18px 16px" }}>
          {/* Project context */}
          <div
            style={{
              background: "#f8fafc",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 12,
              border: "1px solid #f1f5f9",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Context
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#64748b" }}>Project</span>
                <span style={{ color: "#1e293b", fontWeight: 600 }}>{n.projectName}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#64748b" }}>Category</span>
                <span style={{ color: "#1e293b", fontWeight: 500 }}>{n.categoryName}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#64748b" }}>Subcategory</span>
                <span style={{ color: "#1e73be", fontWeight: 600 }}>{n.subcategoryName}</span>
              </div>
            </div>
          </div>

          {/* Target vs Current */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                background: "#f0f7ff",
                borderRadius: 10,
                padding: "10px 12px",
                border: "1px solid rgba(30,115,190,0.1)",
              }}
            >
              <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600, marginBottom: 4, letterSpacing: "0.5px" }}>
                Target
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1e73be" }}>
                {thresholdLabel}
              </div>
            </div>
            <div
              style={{
                background: "rgba(16,185,129,0.06)",
                borderRadius: 10,
                padding: "10px 12px",
                border: "1px solid rgba(16,185,129,0.12)",
              }}
            >
              <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600, marginBottom: 4, letterSpacing: "0.5px" }}>
                Current
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#059669" }}>
                {currentLabel}
              </div>
            </div>
          </div>

          {/* Visual progress bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 11 }}>
              <span style={{ color: "#64748b" }}>Progress</span>
              <span style={{ color: "#1e293b", fontWeight: 600 }}>{(ratio * 100).toFixed(1)}%</span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 4,
                background: "#f1f5f9",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${(ratio * 100).toFixed(1)}%`,
                  background: ratio >= 1
                    ? "linear-gradient(90deg, #10b981, #34d399)"
                    : "linear-gradient(90deg, #1e73be, #3b8fd4)",
                  borderRadius: 4,
                  transition: "width 0.6s ease",
                }}
              />
            </div>
          </div>

          {/* Custom message */}
          {n.customMessage && (
            <div
              style={{
                background: "rgba(245,158,11,0.05)",
                border: "1px solid rgba(245,158,11,0.12)",
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 10, color: "#d97706", fontWeight: 600, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Custom Message
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#475569",
                  fontStyle: "italic",
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                }}
              >
                &ldquo;{n.customMessage}&rdquo;
              </div>
            </div>
          )}

          {/* Triggered by */}
          <div
            style={{
              fontSize: 11,
              color: "#94a3b8",
              marginBottom: 14,
            }}
          >
            Triggered by <span style={{ color: "#1e293b", fontWeight: 500 }}>{n.triggeredBy}</span>
          </div>

          {/* Dismiss button */}
          <button
            onClick={handleClose}
            style={{
              width: "100%",
              padding: "9px 14px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              color: "#64748b",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#f1f5f9";
              (e.currentTarget as HTMLElement).style.color = "#475569";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#f8fafc";
              (e.currentTarget as HTMLElement).style.color = "#64748b";
            }}
          >
            Dismiss
          </button>
        </div>

        {/* Animations */}
        <style>{`
          @keyframes notifPopupEnter {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0) scale(1);
            }
          }
          @keyframes notifPopupExit {
            from {
              opacity: 1;
              transform: translateX(-50%) translateY(0) scale(1);
            }
            to {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px) scale(0.95);
            }
          }
          @keyframes notifBackdropEnter {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes notifBackdropExit {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        `}</style>
      </div>
    </>,
    document.body
  );
}
